import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/context/AppContext";
import { apiGet } from "@/lib/api";
import { toast } from "sonner";
import { Search, Globe2, Loader2, ExternalLink, ArrowUpRight, CalendarDays, Building2, Wand2 } from "lucide-react";

export default function LiveGrants() {
    const { startApplication, applications } = useApp();
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(null);

    const search = async (keyword) => {
        setLoading(true);
        try {
            const d = await apiGet(`/grants/search${keyword ? `?keyword=${encodeURIComponent(keyword)}` : ""}`);
            setRows(d.hits || []);
            setTotal(d.total || 0);
            if (!d.hits?.length) toast.message("No live results for that search. Try a broader keyword.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Grants.gov is unreachable right now");
        } finally { setLoading(false); }
    };

    useEffect(() => { search(""); }, []);

    const startFromLive = async (hit) => {
        try {
            setStarting(hit.id);
            await startApplication({
                type: "grant", target_id: hit.id, target_title: hit.title,
                target_amount: 0, target_meta: { source: "grants.gov", agency: hit.agency, number: hit.opportunity_number, url: hit.url },
            });
            toast.success("Application opened", { description: "Open it from Applications to let AI prepare the packet." });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't open the application");
        } finally { setStarting(null); }
    };

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Federal · Grants.gov</p>
                    <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Live grants from Grants.gov</h1>
                    <p className="mt-3 text-muted-foreground max-w-2xl">Every federal opportunity posted to Grants.gov — updated live. Click any grant to open it as a draft; Owed AI will prepare the packet from your vault.</p>
                </div>
                <Badge variant="outline" className="bg-secondary/60 border-border rounded-full px-3 py-1" data-testid="livegrants-total">
                    <Globe2 className="h-3.5 w-3.5 mr-1.5" /> {total.toLocaleString()} live opportunities
                </Badge>
            </div>

            <Card className="mt-8 p-3 border-border flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0 rounded-full border border-border bg-secondary/40 px-4 h-10">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") search(q); }}
                        placeholder="Search: small business, research, veteran, housing…"
                        className="border-0 bg-transparent h-9 focus-visible:ring-0 px-0 text-sm placeholder:text-muted-foreground"
                        data-testid="livegrants-search-input"
                    />
                </div>
                <Button onClick={() => search(q)} disabled={loading} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="livegrants-search-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                </Button>
            </Card>

            {loading ? (
                <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : rows.length === 0 ? (
                <Card className="mt-8 p-12 text-center border-dashed border-border">
                    <Globe2 className="h-6 w-6 text-muted-foreground mx-auto" />
                    <p className="mt-3 text-muted-foreground">No opportunities surfaced. Try a broader keyword.</p>
                </Card>
            ) : (
                <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {rows.map((hit) => {
                        const existing = applications.find(a => a.target_id === hit.id);
                        return (
                            <Card key={hit.id} className="p-6 border-border flex flex-col h-full hover:border-foreground/30 transition-colors" data-testid={`livegrants-card-${hit.id}`}>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Building2 className="h-3.5 w-3.5" />
                                    <span className="truncate">{hit.agency || "—"}</span>
                                </div>
                                <h3 className="mt-2 font-serif text-xl leading-tight text-balance line-clamp-3">{hit.title}</h3>
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className="bg-secondary/60 border-border text-xs">{hit.status || "open"}</Badge>
                                    {hit.opportunity_number && <Badge variant="outline" className="bg-secondary/60 border-border text-xs font-mono">{hit.opportunity_number}</Badge>}
                                </div>
                                <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                                    {hit.close_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Closes {hit.close_date}</span>}
                                </div>
                                <div className="mt-auto pt-5 flex items-center gap-2">
                                    <a href={hit.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                                        Grants.gov <ExternalLink className="h-3 w-3" />
                                    </a>
                                    {existing ? (
                                        <Button asChild size="sm" variant="outline" className="rounded-full ml-auto">
                                            <a href={`/app/detail/grant/${hit.id}`}>Resume <ArrowUpRight className="h-3.5 w-3.5 ml-1" /></a>
                                        </Button>
                                    ) : (
                                        <Button size="sm" onClick={() => startFromLive(hit)} disabled={starting === hit.id} className="rounded-full ml-auto bg-foreground text-background hover:bg-foreground/90" data-testid={`livegrants-start-${hit.id}`}>
                                            {starting === hit.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Wand2 className="h-3.5 w-3.5 mr-1" /> Start + AI prep</>)}
                                        </Button>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
